import { useEffect, useState, useCallback } from "react"
import styles from "./styles.module.scss"
import cn from "clsx"
import type { FC, HTMLAttributes } from "react"

interface Dimmensions {
  width: number,
  height: number,
}

interface LoaderProps
extends HTMLAttributes<SVGSVGElement> {

}

const triangleLimit = 100
const initialDimmenions: Dimmensions = {
  width: 0,
  height: 0,
}

export const Loader: FC<LoaderProps> = ({ className, ...rest }) => {
  const [dimmestions, setDimmensions] = useState<Dimmensions>(initialDimmenions)
  const [isReady, setReadyState] = useState(false)
  const resizeHandler = useCallback(() => void setDimmensions({
    width: window.innerWidth,
    height: window.innerHeight,
  }), [setDimmensions])
  const center: Dimmensions = {
    height: dimmestions.height / 2,
    width: dimmestions.width / 2,
  }
  const V = Math.min(...Object.values(dimmestions))
  const R = Math.min(V / 4, triangleLimit)
  const r = R / 2
  const l = V * .035

  useEffect(() => {
    resizeHandler()
    window.addEventListener('resize', resizeHandler, { passive: true })
    return () => window.removeEventListener('resize', resizeHandler)
  }, [resizeHandler])

  useEffect(() => {
    const timeout = setTimeout(setReadyState.bind(0, true), 6000)
    return () => clearTimeout(timeout)
  }, [])

  useEffect(() => {
    const handler = (event: Event) => event.preventDefault()
    if (isReady) window.addEventListener('scroll', handler)
    else window.removeEventListener('scroll', handler)
    return () => window.removeEventListener('scroll', handler)
  }, [isReady])



  const halfSide = Math.sqrt(R**2 - r**2)
  const tr = [[
    center.width,
    center.height + R
  ],[
    center.width - halfSide,
    center.height - r
  ], [
    center.width + halfSide,
    center.height - r
  ]]

  return (
    <svg {...rest} {...dimmestions} className={cn(styles.loader, className, {
      [styles.hidden]: isReady,
    })}>
      <g className={styles.triangles}>
        {new Array(2).fill(true).map((_, index) => (
          <polygon key={index} points={tr.join(' ')} pathLength="300" />
        ))}
      </g>
      <g className={styles.linesIn}>
        {new Array(3).fill(true).map((_, index) => (
          <line key={index}
            pathLength="100"
            transform={`rotate(${index * 120}, ${center.width}, ${center.height})`}
            x1={center.width} 
            y1={center.height + R} 
            x2={center.width} 
            y2={dimmestions.height * 2} />
        ))}
      </g>
      <g className={styles.linesOut}>
        {new Array(3).fill(true).map((_, index) => (
          <line key={index}
            stroke="red"
            pathLength="100"
            transform={`rotate(${index * 120}, ${center.width}, ${center.height})`}
            x1={center.width} 
            y1={center.height - r} 
            x2={center.width} 
            y2={-dimmestions.height * 2} />
        ))}
      </g>
      <svg className={styles.letter} viewBox="0 0 11 9"
        x={center.width - l / 2} 
        y={center.height - l / 2} 
        width={l} height={l}>
        <path d="M0 0H1.58589L5.5 6.40491L9.41411 0H11L5.5 9L0 0Z" 
          pathLength="100" />
      </svg>
    </svg>
  )
}
